/**
 * @fileoverview Conjunto de coordenadas centinela detectadas
 * empíricamente en `/edu-centers/{ine5}.json.gz` del Worker R2.
 *
 * Procedencia (auditoría empírica 11-may-2026, muestra estratificada
 * de 10 buckets INE × 1945 features):
 *   - `.ptel/AUDIT_XVAL_EDU_SUBA_20260511.md` §4.1
 *   - `.ptel/AUDIT_EDU_SUBB_CONSUMERS_20260511.md` §4.1
 *
 * Cifra agregada: 19 coords distintas × 95 centros = 4,88 % muestra.
 *
 * El sentinel principal `(373974.1, 4148235.3)` ≈ Lucena/Aguilar
 * (Córdoba) agrupa 50 centros que cruzan 8 buckets INE distintos
 * (04013, 11012, 14021, 18087, 21041, 23050, 29067, 41091) — confirma
 * que es un fallback global del ETL upstream `junta_educacion`, no un
 * centroide municipal.
 *
 * Política de revisión: re-auditar tras cada actualización del
 * snapshot `da_centros.csv` Junta. Si el sentinel principal cambia
 * de coordenada, regenerar este fichero. Trazabilidad upstream:
 * `OBS-FT-EDU-R2-SENTINEL-ETL-CLEANUP` (sev C, derivada de sub-C).
 *
 * @version 1.0.0
 * @date 2026-05-12
 */

/**
 * Pares `[x, y]` UTM30 (EPSG:25830) de los 19 centroides sentinel.
 * Valores empíricos exactos con redondeo a 0,1 m (precisión nativa
 * del ETL upstream).
 *
 * Comentario por entrada: `N=<centros>, B=<buckets INE distintos>`.
 */
const SENTINEL_COORDS_ARRAY: ReadonlyArray<readonly [number, number]> = [
  [373974.1, 4148235.3], // N=50, B=8 — principal (Lucena/Aguilar, Córdoba)
  [237151.3, 4141832.9], // N=6,  B=1 — Sevilla centro histórico (41091)
  [371810.7, 4064848.8], // N=5,  B=1 — Málaga (29067)
  [446360.3, 4114909.7], // N=4,  B=1 — Granada (18087)
  [548864.2, 4077748.6], // N=2,  B=1 — Almería (04013)
  [548480.9, 4076885.3], // N=2,  B=1 — Almería (04013)
  [345254.9, 4194689.5], // N=2,  B=1 — Córdoba (14021)
  [331647.5, 4190650.6], // N=2,  B=1 — Córdoba (14021)
  [365286.0, 4059680.1], // N=2,  B=1 — Málaga (29067)
  [370441.8, 4064627.0], // N=2,  B=1 — Málaga (29067)
  [373251.6, 4068105.9], // N=2,  B=1 — Málaga (29067)
  [370511.8, 4062590.0], // N=2,  B=1 — Málaga (29067)
  [371078.1, 4062211.7], // N=2,  B=1 — Málaga (29067)
  [236800.7, 4141050.9], // N=2,  B=1 — Sevilla (41091)
  [238248.7, 4141459.9], // N=2,  B=1 — Sevilla (41091)
  [235071.8, 4141925.9], // N=2,  B=1 — Sevilla (41091)
  [239228.6, 4139638.0], // N=2,  B=1 — Sevilla (41091)
  [238578.7, 4143378.9], // N=2,  B=1 — Sevilla (41091)
  [236410.8, 4144662.8], // N=2,  B=1 — Sevilla (41091)
] as const;

/**
 * Set indexado para lookup `O(1)`. Clave:
 * `` `${x.toFixed(1)},${y.toFixed(1)}` ``.
 *
 * Encapsulado: usar siempre `isSentinelCoord(x, y)` desde código
 * llamador para que el formato de clave permanezca como detalle
 * interno.
 */
export const EDU_SENTINEL_COORDS: ReadonlySet<string> = new Set(
  SENTINEL_COORDS_ARRAY.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`),
);

/**
 * Comprueba si una coordenada UTM30 (EPSG:25830) pertenece al
 * conjunto de coords centinela detectadas en R2 EDU.
 *
 * Tolerancia: comparación exacta con redondeo a 0,1 m (las coords
 * sentinel son determinísticas en el ETL upstream, no varían entre
 * snapshots).
 *
 * @param x coordenada X UTM30 en metros.
 * @param y coordenada Y UTM30 en metros.
 * @returns `true` si la coord es sentinel; `false` en caso contrario.
 */
export function isSentinelCoord(x: number, y: number): boolean {
  return EDU_SENTINEL_COORDS.has(`${x.toFixed(1)},${y.toFixed(1)}`);
}
